<template>
  <div
    class="keywords"
    :class="{ 'adjust-container': isFullScreenView && !$vuetify.breakpoint.xsOnly }"
  >
    <v-row
      class="keywords-head-options"
      :class="{ 'px-0 pt-0 px-sm-10 pt-sm-10': !isFullScreenView }"
      no-gutters
    >
      <v-col
        v-if="isOnboarding"
        class="col-auto pb-2 pr-2 d-flex align-center"
      >
        <ph-caret-left
          weight="light"
          size="24"
          class="clickable grey--text text--lighten-1"
          @click="goBackToWelcomeSteps"
        />
      </v-col>
      <v-col
        v-if="isOnboarding"
        class="col-8 pb-2 text-left"
      >
        <h6
          class="text text-l-large-bold grey--text text--darken-1 clickable"
          @click="goBackToWelcomeSteps"
        >
          Back to Specialties
        </h6>
      </v-col>
      <v-col
        v-if="isGuestUser && !$vuetify.breakpoint.xsOnly"
        class="col-8 pb-2 text-left"
      >
        <h6 class="text text-l-large-bold grey--text text--darken-1">Select interests</h6>
      </v-col>
      <v-col
        v-if="isGuestUser && !$vuetify.breakpoint.xsOnly"
        class="col-4 d-flex align-center justify-end"
      >
        <ph-x
          weight="thin"
          size="32"
          class="clickable grey--text text--lighten-1 close-icon-position"
          @click="$emit('close')"
        />
      </v-col>
      <v-col
        cols="12"
        :class="{ 'pt-4 pt-sm-0': isFullScreenView }"
      >
        <p
          class="text grey--text text--darken-1 mb-0"
          :class="$vuetify.breakpoint.width < 375 ? 'text-l-small-regular' : 'text-l-large-regular'"
        >
          Select any specific interests you may have to obtain better article recommendations.
        </p>
      </v-col>
      <v-col class="col-12 pt-4">
        <v-text-field
          v-model="search"
          class="keywords-search"
          solo
          height="54px"
          hide-details
          placeholder="Search interest"
        >
          <template #prepend-inner>
            <ph-magnifying-glass
              weight="thin"
              size="24"
              class="grey--text text--lighten-2 mr-2"
            />
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <div class="keywords-container">
      <v-row
        class="justify-center mx-0 mx-sm-0"
        :class="{ 'chips-container': !isFullScreenView }"
        no-gutters
      >
        <v-col
          v-if="loadingKeywords"
          cols="12"
        >
          <ProgressBarComponent :title="'Keywords'" />
        </v-col>
        <v-col
          cols="12"
          class="px-0 pt-0"
        >
          <v-row
            v-if="!loadingKeywords"
            class="max-height"
            dense
            no-gutters
          >
            <v-col
              cols="12"
              class="max-height mb-10 mb-sm-0"
            >
              <div
                v-if="keywordsToDisplay.length > 0"
                class="text-left mt-0 py-1 px-1 mb-15 mb-sm-0"
              >
                <KeywordChipComponent
                  v-for="keyword in keywordsToDisplay"
                  :key="keyword + uniqueKeysHelper()"
                  :keyword="keyword"
                  :user-keywords="userKeywords"
                  :is-big-mode="true"
                  @subscribe-keyword="handleSubscribeToKeyword"
                  @unsubscribe-keyword="handleUnsubscribeToKeyword"
                />
                <div v-if="!loadingKeywords && loadingMoreKeywords">
                  <v-col
                    cols="12"
                    class="text-center mobile pb-10"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    />
                  </v-col>
                </div>
              </div>
              <div
                v-if="search && keywordsToDisplay.length === 0"
                class="no-keywords-found-container"
                :class="$vuetify.breakpoint.height <= 470 ? 'align-start' : 'align-center'"
              >
                <div
                  class="row justify-center"
                  :class="{ 'mb-15': $vuetify.breakpoint.height <= 568 }"
                >
                  <div class="col col-11 col-sm-6 col-md-6 text-center">
                    <SearchNoFoundIconComponent
                      :id="4"
                      :key="uniqueKeysHelper()"
                    />
                    <p class="text text-m-large-regular grey--text text--darken-1 mt-2">
                      <span class="text text-m-large-bold">There’s no results matching your interests</span>.
                      <br v-if="$vuetify.breakpoint.width >= 375" />Try searching using different words or just type
                      another interest.
                    </p>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <v-row
      v-if="!loadingKeywords && !isFullScreenView"
      class="sticky-actions-container px-6 pb-6 pt-sm-2 px-sm-10 pb-sm-10 justify-center d-flex"
      no-gutters
    >
      <v-card
        class="actions-component-sticky"
        flat
      >
        <v-row
          justify="center"
          justify-sm="end"
          align="center"
          no-gutters
        >
          <div class="col-12 col-sm-auto mr-sm-4">
            <v-btn
              ripple
              depressed
              block
              height="44px"
              color="grey lighten-5"
              class="primary--text heading heading-s-small align-self-center unaltered-text-cap letter-spacing-normal shadow-light-button px-6"
              @click="skipKeywordsSelection"
            >
              Do it later
            </v-btn>
          </div>
          <div class="col-12 col-sm-auto">
            <v-btn
              :disabled="currentSelectedKeywordsCount === 0"
              ripple
              depressed
              block
              height="44px"
              color="primary"
              class="heading heading-s-small text-capitalize px-7 white--text mb-4 mb-sm-0"
              @click.stop="finishKeywordsSelection"
            >
              Continue
            </v-btn>
          </div>
        </v-row>
      </v-card>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import ProgressBarComponent from '@/components/common/ProgressBarComponent';
import KeywordChipComponent from '@/components/common/KeywordChipComponent';
import SearchNoFoundIconComponent from '@/assets/SearchNoFoundIconComponent';
import { PhCaretLeft, PhMagnifyingGlass, PhX } from 'phosphor-vue';

import { useFeatureFlags, MILESTONE_ONE_FEED_ENABLED } from '@/composables/useFeatureFlags';
import { useSubscribeUserToKeywords } from '@/composables/keywords/useSubscribeUserToKeywords';
import { useUnsubscribeUserToKeywords } from '@/composables/keywords/useUnsubscribeUserToKeywords';
import { useUserKeywords } from '@/composables/keywords/useUserKeywords';

export default {
  components: {
    ProgressBarComponent,
    KeywordChipComponent,
    PhCaretLeft,
    PhMagnifyingGlass,
    PhX,
    SearchNoFoundIconComponent,
  },
  props: ['isGuestUser', 'checkForGuestSpecialties', 'hasToShowToast', 'hasToUseInnerScroll'],
  setup() {
    const { milestoneOneFeedEnabled } = useFeatureFlags([MILESTONE_ONE_FEED_ENABLED]);
    const { subscribeUserToKeywords } = useSubscribeUserToKeywords();
    const { unsubscribeUserToKeywords } = useUnsubscribeUserToKeywords();
    const { userKeywords, refetch } = useUserKeywords();

    return {
      milestoneOneFeedEnabled,
      refetchUserKeywords: refetch,
      subscribeUserToKeywords,
      unsubscribeUserToKeywords,
      userKeywords,
    };
  },
  data: () => ({
    search: null,
    allKeywords: null,
    loadingKeywords: true,
    loadingMoreKeywords: false,
    canLoadMoreArticles: true,
    keywordsToSubscribe: [],
    keywordsToUnsubscribe: [],
    unselectedKeywords: [],
    uniqueKeysHelper: uuidv4,
    isFullScreenView: false,
    showToast: true,
    currentPaginate: 1,
    nextPaginationForSearch: null,
    minKeywordSearchLength: 2,
  }),
  computed: {
    ...mapGetters('auth', ['currentUser']),
    currentSelectedKeywordsCount() {
      return this.userKeywords?.length || 0;
    },
    keywordsToDisplay() {
      return [...this.userKeywords, ...this.unselectedKeywords];
    },
    isOnboarding() {
      return !this.isGuestUser && !this.$vuetify.breakpoint.xsOnly && this.$route.name !== 'keywords';
    },
  },
  watch: {
    search: async function () {
      if (this.search !== null && this.search.trim() !== '' && this.search.length > this.minKeywordSearchLength) {
        let cleanSearch = this.search.trim().toLowerCase().replace(/\s+/g, ' ');

        this.loadingMoreKeywords = true;
        this.allKeywords = null;
        this.nextPaginationForSearch = null;
        const response = await this.searchByTerm({
          params: { term: cleanSearch, usePagination: true, cursor: this.nextPaginationForSearch },
        });

        if (response.data?.keywords) {
          this.nextPaginationForSearch = response.data.nextPagination;
          this.allKeywords = [...(this.allKeywords?.length ? this.allKeywords : []), ...response.data.keywords];
          this.refreshUnselectedKeywords();
          this.loadingKeywords = false;
          this.loadingMoreKeywords = false;
        }
      } else if (!this.search) {
        this.currentPaginate = 1;
        this.nextPaginationForSearch = null;
        this.allKeywords = null;
        await this.loadKeywords();
      }
    },
    userKeywords: function () {
      this.refreshUnselectedKeywords();
    },
  },
  async mounted() {
    if (this.$route.name === 'keywords') {
      this.isFullScreenView = true;
      this.hideTopMenu(true);
      this.setTopMenuTitle('Back to Profile');
    }

    if (this.currentUser) {
      await this.loadKeywords();
    }

    await this.scroll();

    if (this.hasToShowToast) {
      this.showToast = false;
      return;
    }
    this.showToast = true;
  },
  methods: {
    ...mapActions('keywords', [
      'getAllKeywords',
      'subscribeKeywordsToUser',
      'unsubscribeKeywordsToUser',
      'searchByTerm',
    ]),
    ...mapActions('menu', ['hideTopMenu', 'setTopMenuTitle']),
    async loadKeywords() {
      let response = null;

      if (this.search?.length > this.minKeywordSearchLength) {
        let cleanSearch = this.search.trim().toLowerCase().replace(/\s+/g, ' ');
        response = await this.searchByTerm({
          params: { term: cleanSearch, usePagination: true, cursor: this.nextPaginationForSearch },
        });
        this.nextPaginationForSearch = response.data?.nextPagination ?? null;
        this.canLoadMoreArticles = this.nextPaginationForSearch != null;
      } else {
        response = await this.getAllKeywords({ params: { currentPaginate: this.currentPaginate } });
        this.canLoadMoreArticles = response.data?.hasMoreToPaginate;
      }

      if (response.data?.keywords) {
        this.allKeywords = [...(this.allKeywords?.length ? this.allKeywords : []), ...response.data.keywords];
        this.refreshUnselectedKeywords();
        this.loadingKeywords = false;
        this.loadingMoreKeywords = false;
        this.currentPaginate = this.currentPaginate + 1;
      }
    },
    async handleSubscribeToKeyword(keyword) {
      if (!this.currentUser) return;

      if (this.milestoneOneFeedEnabled) {
        this.subscribeUserToKeywords({ keywords: [keyword] });
      } else {
        this.keywordsToSubscribe.push(keyword);

        const response = await this.subscribeKeywordsToUser({
          keywords: this.keywordsToSubscribe,
          showToast: this.showToast,
        });

        if (!response.data) return;

        this.refetchUserKeywords();
        this.refreshUnselectedKeywords();
        this.keywordsToSubscribe = [];
      }
    },
    async handleUnsubscribeToKeyword(keyword) {
      if (!this.currentUser) return;

      if (this.milestoneOneFeedEnabled) {
        this.unsubscribeUserToKeywords({ keywords: [keyword] });
      } else {
        this.keywordsToUnsubscribe.push(keyword);

        const response = await this.unsubscribeKeywordsToUser({
          keywords: this.keywordsToUnsubscribe,
          showToast: this.showToast,
        });

        if (!response.data) return;

        this.refetchUserKeywords();
        this.refreshUnselectedKeywords();
        this.keywordsToUnsubscribe = [];
      }
    },
    refreshUnselectedKeywords() {
      this.unselectedKeywords = this.allKeywords?.filter((keyword) => {
        return this.userKeywords?.indexOf(keyword) < 0;
      });
    },
    skipKeywordsSelection() {
      this.$emit('skip-keywords-selection');
    },
    finishKeywordsSelection() {
      this.$emit('finish-keywords-selection');
    },
    goBackToWelcomeSteps() {
      this.$emit('show-again-welcome-steps');
    },
    scroll() {
      if (this.hasToUseInnerScroll) {
        let scrollContainerInit = document.querySelectorAll('.keywords')[0].closest('.row');

        scrollContainerInit.onscroll = () => {
          let scrollContainer = document.querySelectorAll('.keywords')[0].closest('.row');

          let maxScroll = scrollContainer.scrollTopMax;
          let currentScroll = scrollContainer.scrollTop;

          let isCloseTobottomOfWindow = currentScroll + 100 >= maxScroll;

          if (isCloseTobottomOfWindow && !this.loadingMoreKeywords && this.canLoadMoreArticles) {
            console.log(currentScroll, maxScroll);
            this.loadingMoreKeywords = true;
            this.$_debounce(async () => {
              await this.loadKeywords();
            }, 3000)();
          }
        };
      }

      if (!this.hasToUseInnerScroll) {
        window.onscroll = () => {
          let currentScroll = document.documentElement.scrollTop + window.innerHeight;
          let totalHeightViewport = document.documentElement.offsetHeight;

          let isCloseTobottomOfWindow = currentScroll + 100 >= totalHeightViewport;

          if (isCloseTobottomOfWindow && !this.loadingMoreKeywords && this.canLoadMoreArticles) {
            this.loadingMoreKeywords = true;
            this.$_debounce(async () => {
              await this.loadKeywords();
            }, 3000)();
          }
        };
      }
    },
  },
};
</script>
<style scoped>
.keywords {
  width: 100%;
}
.keywords-head-options {
  position: absolute;
  top: 0px;
  right: 0px;
  width: 100%;
  z-index: 10;
  background: var(--v-background-base);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
.actions-component-sticky {
  height: 52px;
  width: 100%;
  background-color: transparent !important;
}
.keywords-container {
  margin-top: 150px;
  position: relative;
}
.chips-container {
  overflow: visible;
}
@media (max-width: 600px) {
  .keywords-head-options {
    position: initial;
  }
  .keywords-container {
    margin-top: 24px;
    margin-bottom: 0px;
  }
  .actions-component-sticky {
    position: fixed;
    bottom: 70px;
    width: 90%;
  }
  .actions-component-sticky > .row {
    flex-direction: column-reverse;
  }
  .chips-container {
    padding-bottom: 200px !important;
  }
}
@media (min-width: 600px) {
  .keywords-container {
    margin-top: 174px;
    margin-bottom: 40px;
  }
  .sticky-actions-container {
    position: absolute;
    bottom: 0px;
    right: 0px;
    width: 100%;
    background-color: var(--v-background-base);
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  .actions-component-sticky {
    padding: 0 !important;
  }
  .actions-component-sticky > .row {
    background-color: transparent;
    margin-left: 0;
    margin-right: 0;
  }
  .adjust-container {
    margin: auto;
    width: 672px;
    background: var(--v-card-base);
    border-radius: 8px;
    padding: 24px;
    margin-top: 48px;
  }
  .adjust-container .keywords-head-options {
    background: var(--v-card-base);
    position: initial;
  }
  .adjust-container .keywords-container {
    margin-top: 24px;
    margin-bottom: 0px;
  }
}
@media (min-width: 1024px) {
  .adjust-container {
    margin: auto;
    width: 784px;
    background: var(--v-card-base);
    border-radius: 8px;
    padding: 24px 32px 32px 32px;
    margin-top: 32px;
  }
  .keywords-container {
    margin-top: 150px;
    margin-bottom: 40px;
  }
  .theme-chip {
    width: 45%;
  }
}
@media (min-width: 1264px) {
  .adjust-container {
    margin: auto;
    width: 792px;
    background: var(--v-card-base);
    border-radius: 8px;
    padding: 32px 48px 40px 48px;
    margin-top: 64px;
  }
}
.keywords-search >>> .v-input__slot {
  background: var(--v-background-base) !important;
  box-shadow: inset -3px -3px 3px rgba(255, 255, 255, 0.7), inset 3px 3px 3px rgba(174, 174, 192, 0.2) !important;
  border-radius: 4px;
}
.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
  background-color: var(--v-grey-lighten2) !important;
}
.theme--light.v-btn.v-btn--disabled {
  color: var(--v-grey-darken1) !important;
}
.no-keywords-found-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  flex-wrap: wrap;
}
.no-keywords-found-container svg {
  flex: 100%;
}
</style>
